import { Injectable } from '@angular/core';
import { HeaderCellRenderer } from '@syncfusion/ej2-grids';

/**
 * The `@syncfusion/ej2-grids` version whose `HeaderCellRenderer.prepareHeader` this shim was read
 * against and verified on. The shim wraps a private method, so it is a versioned adapter (same
 * contract as `syncfusion-grid-filter-scope.service.ts`): the guard in
 * `syncfusion-grid-action-column.service.spec.ts` fails the suite when the installed version
 * differs, forcing the upgrader to re-read `prepareHeader` / `extendPrepareHeader` in
 * `header-cell-renderer.js` (where the menu and filter icons are decided from `showColumnMenu`,
 * `allowFiltering` and `field`) and bump this pin.
 */
export const VERIFIED_EJ2_GRIDS_VERSION = '34.1.33';

/**
 * Column `field` ids that mark a grid's Action column (compared lower-cased). They are code-level
 * ids written in the templates (`field='action'` / `field='Action'`), never translated text, so
 * matching on them is locale-independent.
 */
export const ACTION_COLUMN_FIELDS: ReadonlySet<string> = new Set(['action', 'actions']);

/** The slice of a Syncfusion `Column` this shim reads and writes. */
interface ColumnInternals {
  field?: string;
  showColumnMenu?: boolean;
  allowFiltering?: boolean;
  allowSorting?: boolean;
}

/** The slice of `HeaderCellRenderer` this shim wraps. */
interface HeaderCellRendererInternals {
  prepareHeader(cell: { column?: ColumnInternals }, node: Element, fltrMenuEle: Element): Element;
}

/**
 * Whether a column is a grid's Action column, i.e. its `field` is one of {@link ACTION_COLUMN_FIELDS}.
 * @param column - The Syncfusion column (may be undefined).
 * @returns True for an Action column.
 */
export function isActionColumn(column: ColumnInternals | undefined): boolean {
  return typeof column?.field === 'string' && ACTION_COLUMN_FIELDS.has(column.field.toLowerCase());
}

/**
 * Removes the column-menu icon from every grid's Action column.
 *
 * Syncfusion draws the menu icon on any column that has a `field`
 * (`HeaderCellRenderer.extendPrepareHeader`); columns without one get none. Many Action columns
 * carry `field='action'` / `'Action'` (≈40 grids), so they showed a menu whose Sort / Filter
 * entries act on a field no row holds. Turning only `showColumnMenu` off is not enough: the same
 * renderer then draws the plain filter icon instead (`prepareHeader`: filtering on, field set,
 * column menu off), so filtering and sorting are switched off on the column too. They never
 * worked on it: no row carries the field.
 *
 * Patching the shared renderer, rather than adding `[showColumnMenu]="false"` per template, is
 * what covers every grid, including tags that span several lines and grids added later.
 * Columns whose field is anything else are untouched.
 */
@Injectable({ providedIn: 'root' })
export class SyncfusionGridActionColumnService {

  /** Guard so the prototype patch is installed exactly once per page load. */
  private static installed = false;

  /**
   * Installs the Action-column shim. Safe to call repeatedly; only the first call patches.
   * @returns Nothing.
   */
  install(): void {
    if (SyncfusionGridActionColumnService.installed) {
      return;
    }
    SyncfusionGridActionColumnService.installed = true;

    // prepareHeader is internal to the grid's header renderer; the cast is required to wrap it.
    const proto = HeaderCellRenderer.prototype as unknown as HeaderCellRendererInternals;
    const originalPrepareHeader = proto.prepareHeader;

    // Version guard: a Syncfusion upgrade that renames this internal must leave the icon in
    // place, never break header rendering.
    if (typeof originalPrepareHeader !== 'function') {
      return;
    }

    proto.prepareHeader = function (this: HeaderCellRendererInternals, cell, node, fltrMenuEle): Element {
      const column = cell?.column;
      if (column && isActionColumn(column)) {
        column.showColumnMenu = false;
        column.allowFiltering = false;
        column.allowSorting = false;
      }
      return originalPrepareHeader.call(this, cell, node, fltrMenuEle);
    };
  }
}
